import { useMutation } from '@apollo/client';
import { DeletePublicApiKeyInput } from '@provision/entity-types';
import { useCallback } from 'react';
import {
  DELETE_PUBLIC_API_KEY_MUTATION,
  GET_PUBLIC_API_KEYS_QUERY,
} from 'src/graphql/entities/public-api-key/public-api-key.operations';
import { updateQueryWithInvalidation } from 'src/graphql/entities/public-api-key/public-api-key.utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

interface DeletePublicApiKeyMutation {
  deletePublicApiKey: boolean;
}

interface DeletePublicApiKeyMutationVariables {
  params: DeletePublicApiKeyInput;
}

export const useDeletePublicApiKey = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [deleteMutation, { loading: isDeleting }] = useMutation<
    DeletePublicApiKeyMutation,
    DeletePublicApiKeyMutationVariables
  >(DELETE_PUBLIC_API_KEY_MUTATION, {
    refetchQueries: [GET_PUBLIC_API_KEYS_QUERY],
    awaitRefetchQueries: true,
    update: updateQueryWithInvalidation(selectedPartnerId!),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to revoke API key, please try again');
    },
  });

  const deletePublicApiKey = useCallback(
    async (apiKeyId: string) => {
      if (!selectedPartnerId) {
        throw new Error('Cannot revoke API key without a selected partner.');
      }
      await deleteMutation({
        variables: {
          params: { partnerId: selectedPartnerId, apiKeyId },
        },
      });
    },
    [deleteMutation, selectedPartnerId],
  );

  return { deletePublicApiKey, isDeleting };
};
